

















































































import { defineComponent, computed, PropType, ref } from '@nuxtjs/composition-api';
import ProductDetailInfoCell from '~/components/pages/Product/Detail/ProductDetailInfoCell.vue';
import ProductDetailLeaflet from '~/components/pages/Product/Detail/ProductDetailLeaflet.vue';
import ProductDetailListItem from '~/components/pages/Product/Detail/ProductDetailListItem.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import DynamicComponent from '~/components/partials/Content/ContentDynamicComponent.vue';
import ProducingAreaFlag from '~/components/partials/ProducingArea/ProducingAreaFlag.vue';
import { useElementLayout } from '~/composables/useElementLayout';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import { formatProducingArea, formatKeyValueArray } from '~/utils/modules/products';
import {
  ProductShowCMSResponseSchema,
  ProductShowECResponseSchema,
  ProductDetailWineSetItem,
  ProductSetItems,
} from '~/types/modules/product';
import { ProductCategoryId, ProductSetType, DisplayLang } from '~/enums';

export default defineComponent({
  components: {
    AppImage,
    AppIcon,
    ProducingAreaFlag,
    ProductDetailInfoCell,
    ProductDetailListItem,
    DynamicComponent,
    ProductDetailLeaflet,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
    cmsResponse: {
      type: Object as PropType<ProductShowCMSResponseSchema | null>,
      default: null,
    },
    /** もっと詳細を見るを開いているかどうか */
    isOpenMoreDetail: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { currentLang } = useWOVNio();
    const productImage = ref<HTMLDivElement>();
    const setItemVariant = computed(() => props.ecResponse.product_variants.find((variant) => variant.set_items));
    const productCategoryId = computed(() => props.ecResponse.categories[0]?.id || ProductCategoryId.WINE);
    const isVisibleBottle = computed(() =>
      [
        ProductCategoryId.WINE,
        ProductCategoryId.OTHER_ALCOHOL,
        ProductCategoryId.NON_ALCOHOL,
        ProductCategoryId.OTHER_DRINK,
      ].includes(productCategoryId.value)
    );
    const productImageRect = useElementLayout(productImage);
    const imgOffset = computed(() => {
      return `${productImageRect.value.width}px`;
    });

    const isVisibleListItem = computed(() => {
      return (
        productCategoryId.value === ProductCategoryId.WINE_SET &&
        (props.ecResponse.set_type as ProductSetType) !== ProductSetType.FLEXIBLE_SET
      );
    });

    const isShownSetItems = computed(() => props.ecResponse.product_variants.some((item) => item.set_items));

    const data = computed(() => {
      const { langs, master_settings, product_variants, award_information } = props.ecResponse;

      return {
        productImage: props.ecResponse?.product_image_2,
        backgroundImage: props.ecResponse?.background_image,
        name: langs?.find((lang) => lang?.id === 'ja')?.name,
        nameEn: langs?.find((lang) => lang?.id === 'en')?.name,
        producerName: master_settings?.producer?.name_kana,
        producingArea: formatProducingArea(master_settings?.producing_areas),
        dynamicParts: props.cmsResponse?.__dynamic_parts || [],
        awardInformation: award_information,
        /** 子商品情報：単一ヴィンテージの場合のみ表示する */
        howAging: product_variants.length === 1 && product_variants[0].how_aging,
        tasteReview: product_variants.length === 1 && product_variants[0].taste_review,
        whenToDrink: product_variants.length === 1 && product_variants[0].when_to_drink,
        etiquetteImage: product_variants.length === 1 && product_variants[0].etiquette_image,
      };
    });

    const setItems = computed((): ProductDetailWineSetItem[] => {
      if (!setItemVariant.value?.set_items || !isVisibleListItem.value) {
        return [];
      }
      return setItemVariant.value.set_items
        .filter((setItem) => !setItem.is_leaflet)
        .map((setItem) => {
          const {
            langs,
            main_image_url,
            review_settings,
            master_settings,
            price_settings,
            size_amount,
            size_unit,
            attributes,
            three_line_catch_copy,
            taste_review,
          } = setItem;
          const slicedAromas = master_settings.aromas.slice(0, 3);
          return {
            name: langs.find((lang) => lang?.id === 'ja')?.name,
            image: main_image_url,
            reviews: {
              average: review_settings.rating_average,
              count: review_settings.review_total,
            },
            producer: master_settings.producer?.name_kana,
            producingArea: {
              code_id: master_settings.producing_areas?.[0]?.id?.toString() || '',
              areas: master_settings.producing_areas.map((area) => ({ name: area.name })),
            },
            description: three_line_catch_copy,
            tasteReview: taste_review,
            price: {
              isRed: false,
              price: price_settings.price_including_tax,
              priceExcludingTax: price_settings.price_excluding_tax,
            },
            size: size_amount ? `${size_amount}${size_unit || 'ml'}` : undefined,
            color: master_settings.color?.name || '',
            aromas: slicedAromas,
            brands: master_settings.brands,
            bodyPoint: Number(formatKeyValueArray(attributes)?.taste_gauge),
          };
        });
    });

    // リーフレット情報のみ返す
    const leafletInfo = computed((): ProductSetItems[] => {
      if (!setItemVariant.value?.set_items || !isVisibleListItem.value) {
        return [];
      }
      return setItemVariant.value.set_items.filter((setItem) => setItem.is_leaflet);
    });

    const isShownProductNameJa = computed(() => !data.value.nameEn || currentLang.value.code === DisplayLang.JA);

    return {
      emit,
      productImage,
      isVisibleBottle,
      imgOffset,
      isVisibleListItem,
      isShownSetItems,
      data,
      setItems,
      isShownProductNameJa,
      leafletInfo,
    };
  },
});
